import type { ContentSource } from '~/utils/content-normalization/content-normalizer'
import { ELEMENT_NODE, SHOW_TEXT } from '#shared/utils/dom'
import { removeEmptyNodes } from '#shared/utils/html'
import {
  normalizeJats,
  normalizeLawQuotes,
  normalizeLists,
  normalizeLiteratureText,
} from '~/utils/content-normalization/content-normalizer'
import { cleanCorruptedAttributes, processLinks, processNewsLinks, stripEpubResources } from '~/utils/html'

export interface HtmlParserPluginContext {
  content?: Document[]
  source?: ContentSource
  siteUrl?: string
}

export type HtmlParserPlugin = (document: Document, ctx?: HtmlParserPluginContext) => Document

export const PLUGIN_MAP = {
  'sanitize-law-footnotes': (doc) => {
    const children = Array.from(doc.body.childNodes)
    const wrapper = doc.createElement('div')
    for (const child of children) {
      if (child.nodeType === ELEMENT_NODE) {
        const element = child as HTMLElement
        if (!element.classList.contains('footnoteLabel')) {
          wrapper.appendChild(child)
        }
        else {
          element.ariaLabel = 'Fotnote'
        }
      }
      else {
        wrapper.appendChild(child)
      }
    }

    doc.body.appendChild(wrapper)
    return doc
  },
  'process-links': processLinks,
  'strip-epub-resources': stripEpubResources,
  'clean-corrupted-attributes': cleanCorruptedAttributes,
  'anchor-target-blank': anchorTargetBlank,
  'process-news-links': processNewsLinks,
  'remove-empty-nodes': removeEmptyNodes,
  'setup-footnote-containers': setupFootnoteContainers,
  'setup-journal-footnote-containers': setupJournalFootnoteContainers,
  'insert-footnotes': insertFootnotes,
  'create-footnote-content': createFootnoteContent,
  'create-journal-footnote-content': createJournalFootnoteContent,

  // --- Normalization Plugins ---
  'normalize-law-quotes': normalizeLawQuotes,
  'normalize-lists': normalizeLists,
  'normalize-literature-text': normalizeLiteratureText,
  'normalize-jats': normalizeJats,
  'clean-highlight-snippet': (doc) => {
    const LEADING_PUNCTUATION = /^[\s.,;:!?'")\]\p{Pf}]+/u
    const TRAILING_PUNCTUATION = /[\s.,;:!?'"([„\p{Pi}]+$/u

    const walker = doc.createTreeWalker(doc.body, SHOW_TEXT)
    const firstTextNode = walker.nextNode()
    if (firstTextNode?.textContent) {
      firstTextNode.textContent = firstTextNode.textContent.replace(LEADING_PUNCTUATION, '')
    }

    let lastTextNode: Node | null = firstTextNode
    let node = walker.nextNode()
    while (node) {
      lastTextNode = node
      node = walker.nextNode()
    }
    if (lastTextNode?.textContent) {
      lastTextNode.textContent = lastTextNode.textContent.replace(TRAILING_PUNCTUATION, '')
    }

    return doc
  },
} as const satisfies Record<string, HtmlParserPlugin>

export type HtmlParserPluginKey = keyof typeof PLUGIN_MAP

export type HtmlParserPlugins = Array<HtmlParserPluginKey | HtmlParserPlugin>

export type HtmlParser<T> = (input: string | null | undefined, plugins?: HtmlParserPlugins, ctx?: HtmlParserPluginContext) => T

export type DomParser = {
  parseStringToDocument: HtmlParser<Document>
  parseStringToSharedDocument: HtmlParser<Document>
  htmlToText: HtmlParser<string>
}

/**
 * Helper that exposes the ability to create an HTML parser without having to set up a full Nuxt environment, such as in unit tests.
 */
export function createHtmlParser(parse: (input: string) => Document) {
  return (input: string | null | undefined, plugins: HtmlParserPlugins = [], ctx?: HtmlParserPluginContext) =>
    plugins.reduce(
      (acc, plugin) =>
        typeof plugin === 'function'
          ? plugin(acc, ctx)
          : PLUGIN_MAP[plugin](acc, ctx),
      parse(input ?? ''),
    )
}

/**
 * Like `createHtmlParser`, but every call returns the same document with its body replaced, so the next call overwrites the previous result.
 *
 * jsdom builds a selector engine per document on its first query, which costs about 2 ms, and a journal article parses hundreds of sections.
 */
export function createSharedDocumentParser(parse: (input: string) => Document) {
  let shared: Document | undefined

  return createHtmlParser((input) => {
    shared ??= parse('')
    shared.body.replaceChildren(...parse(input).body.childNodes)

    return shared
  })
}

/**
 * Plugin that provides a function to parse a string into an HTML document.
 *
 * Uses the built-in `DOMParser` API on the client side and `jsdom` on the server side to reduce bundle size.
 */
export default defineNuxtPlugin({
  name: 'dom-parser',
  async setup() {
    let parse: (input: string) => Document

    const site = useSiteConfig()

    if (import.meta.client) {
      const parser = new DOMParser()

      parse = input => parser.parseFromString(input, 'text/html')
    }
    else {
      const { JSDOM } = await import('jsdom')
      let parser: DOMParser | undefined

      parse = (input) => {
        // One window per request: building a JSDOM window costs milliseconds, and an article parses hundreds of fragments.
        parser ??= new (new JSDOM('', { url: site.url, contentType: 'text/html' }).window.DOMParser)()

        return parser.parseFromString(input, 'text/html')
      }
    }

    const parseStringToDocument = createHtmlParser(parse)
    const parseStringToSharedDocument = createSharedDocumentParser(parse)

    const htmlToText: HtmlParser<string> = (input, plugins = [], ctx) => {
      if (!input) {
        return ''
      }

      return parseStringToDocument(input, plugins, ctx).body.textContent || ''
    }

    const domParser: DomParser = {
      parseStringToDocument,
      parseStringToSharedDocument,
      htmlToText,
    }

    return {
      provide: domParser,
    }
  },
})
